import React from 'react';
import Button from '../UI/Button';
import Switch from '../UI/Switch';
import { Loader2, Wand2, X, Check, AlertCircle } from 'lucide-react';
import { Voice, isFishVoiceId } from '../../types/campaign';

interface EditingStyle {
  id?: string;
  tempId: string;
  name: string;
  enhancedPromptScript?: string;
  conversationStyle: 'natural' | 'professional' | 'casual';
  personalityType: 'curious' | 'helpful' | 'friendly';
  persuasionApproach: 'gentle' | 'consultative' | 'direct';
  transcriber?: 'deepgram' | '11labs' | 'assembly-ai' | 'azure' | 'gladia' | 'google' | 'openai';
  selectedVoiceId?: string;
  voiceConfig?: any;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface ConversationStyleFormProps {
  style: EditingStyle;
  onStyleChange: (style: EditingStyle) => void;
  onSave: (style: EditingStyle) => void;
  onCancel: () => void;
  onGeneratePrompt: (style: EditingStyle) => void;
  campaignId?: string;
  generatingPrompt: string | null | undefined;
  voices: Voice[];
  loadingVoices: boolean;
  voiceError: string | null;
  isEditing: boolean;
  /** Explains which catalog is loaded (Vapi/11labs vs Telnyx curated). */
  voiceHint?: string;
  voiceLabel?: string;
  /** Only relevant for Vapi engines (speech-to-text). Hidden for Telnyx. */
  showTranscriber?: boolean;
  /** Self-hosted only: switch ElevenLabs vs Fish Audio catalogs. */
  showTtsProviderToggle?: boolean;
  ttsProvider?: 'elevenlabs' | 'fish';
  onTtsProviderChange?: (provider: 'elevenlabs' | 'fish') => void;
}

function hasVoiceSelection(style: EditingStyle): boolean {
  const id = (style.selectedVoiceId || '').trim();
  if (!id) return false;
  if (id === 'custom-json') {
    if (!style.voiceConfig) return false;
    if (typeof style.voiceConfig === 'string') return style.voiceConfig.trim().length > 2;
    return typeof style.voiceConfig === 'object' && Object.keys(style.voiceConfig).length > 0;
  }
  return true;
}

export const ConversationStyleForm: React.FC<ConversationStyleFormProps> = ({
  style,
  onStyleChange,
  onSave,
  onCancel,
  onGeneratePrompt,
  campaignId,
  generatingPrompt,
  voices,
  loadingVoices,
  voiceError,
  isEditing = false,
  voiceHint,
  voiceLabel = 'Select Voice',
  showTranscriber = false,
  showTtsProviderToggle = false,
  ttsProvider = 'elevenlabs',
  onTtsProviderChange,
}) => {
  const voiceMatchesProvider =
    !showTtsProviderToggle ||
    (ttsProvider === 'fish'
      ? isFishVoiceId(style.selectedVoiceId)
      : !isFishVoiceId(style.selectedVoiceId));
  const voiceOk = hasVoiceSelection(style) && voiceMatchesProvider;
  const canSave =
    !!style.name.trim() &&
    !!style.enhancedPromptScript?.trim() &&
    voiceOk &&
    generatingPrompt !== style.tempId;

  return (
    <div className="bg-card rounded-lg p-4 border-2 border-primary/20">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            Agent Name
          </label>
          <input
            type="text"
            value={style.name}
            onChange={(e) =>
              onStyleChange({ ...style, name: e.target.value })
            }
            className="input"
            placeholder="Enter agent name"
          />
        </div>

        {/* Two-column layout for form */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column - Prompt field */}
          <div className="flex flex-col lg:col-span-2">
            <h4 className="text-lg font-semibold text-foreground pb-2 mb-4">
                          Conversation Style Prompt
                        </h4>
            <textarea
              value={style.enhancedPromptScript || ''}
              onChange={(e) =>
                onStyleChange({ ...style, enhancedPromptScript: e.target.value })
              }
              className="textarea flex-1 min-h-[200px]"
              placeholder="Enter custom prompt script or use the generate button..."
            />
          </div>

          {/* Right column - Dropdowns and generate button */}
          <div className="flex flex-col space-y-4">
            <h4 className="flex items-center text-lg font-semibold text-foreground pb-2">
                        <Wand2 className="h-4 w-4 mr-2" /> AI Generate
                        </h4>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Conversation Style
              </label>
              <select
                value={style.conversationStyle}
                onChange={(e) =>
                  onStyleChange({
                    ...style,
                    conversationStyle: e.target.value as 'natural' | 'professional' | 'casual'
                  })
                }
                className="select"
              >
                <option value="natural">Natural</option>
                <option value="professional">Professional</option>
                <option value="casual">Casual</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Personality Type
              </label>
              <select
                value={style.personalityType}
                onChange={(e) =>
                  onStyleChange({
                    ...style,
                    personalityType: e.target.value as 'curious' | 'helpful' | 'friendly'
                  })
                }
                className="select"
              >
                <option value="curious">Curious</option>
                <option value="helpful">Helpful</option>
                <option value="friendly">Friendly</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Persuasion Approach
              </label>
              <select
                value={style.persuasionApproach}
                onChange={(e) =>
                  onStyleChange({
                    ...style,
                    persuasionApproach: e.target.value as 'gentle' | 'consultative' | 'direct'
                  })
                }
                className="select"
              >
                <option value="gentle">Gentle</option>
                <option value="consultative">Consultative</option>
                <option value="direct">Direct</option>
              </select>
            </div>



            <div className="flex-1 flex items-end">
              <Button
                type="button"
                variant="outlined"
                onClick={() => onGeneratePrompt(style)}
                disabled={generatingPrompt === style.tempId}
                className="w-full"
              >
                {generatingPrompt === style.tempId ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Wand2 className="h-4 w-4 mr-2" />
                    Generate AI Prompt
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Voice Configuration Section */}
        <div className="space-y-4">
          <h4 className="text-lg font-medium text-foreground">Voice Configuration</h4>
          
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              {voiceLabel} *
            </label>
            <p className="text-xs text-muted-foreground mb-2">
              {voiceHint ||
                'Voice list follows Bot Engine (Script Configuration), not speech recognition.'}
            </p>
            {showTtsProviderToggle && (
              <select
                value={ttsProvider}
                onChange={(e) =>
                  onTtsProviderChange?.(e.target.value === 'fish' ? 'fish' : 'elevenlabs')
                }
                className="select mb-2"
                aria-label="TTS provider"
              >
                <option value="elevenlabs">ElevenLabs</option>
                <option value="fish">Fish Audio</option>
              </select>
            )}
            {loadingVoices && (
              <div className="flex items-center space-x-2 text-sm text-muted-foreground mb-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Loading voices...</span>
              </div>
            )}
            {voiceError && (
              <div className="flex items-center space-x-2 text-sm text-amber-700 dark:text-amber-400 mb-2">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{voiceError}</span>
              </div>
            )}
            <select
                value={style.selectedVoiceId || ''}
                disabled={loadingVoices}
                onChange={(e) => {
                  const newSelectedVoiceId = e.target.value;
                  const updatedStyle = { ...style, selectedVoiceId: newSelectedVoiceId };

                  if (newSelectedVoiceId === 'custom-json') {
                    if (!style.voiceConfig) {
                      updatedStyle.voiceConfig = {};
                    }
                  } else if (!newSelectedVoiceId) {
                    updatedStyle.voiceConfig = undefined;
                  } else {
                    const voice = voices.find((v) => v.providerId === newSelectedVoiceId);
                    // Vapi path needs { provider, voiceId } (e.g. 11labs). Telnyx uses selectedVoiceId only.
                    if (voice && voice.provider !== 'telnyx') {
                      updatedStyle.voiceConfig = {
                        provider: voice.provider,
                        voiceId: voice.providerId,
                      };
                    } else {
                      updatedStyle.voiceConfig = undefined;
                    }
                  }

                  onStyleChange(updatedStyle);
                }}
                className="select"
              >
                <option value="">Select a voice...</option>
                {voices.map((voice) => (
                  <option
                    key={`${voice.provider}-${voice.providerId || voice.id}`}
                    value={voice.providerId}
                  >
                    {voice.provider === 'elevenlabs'
                      ? `${voice.name}${voice.gender && voice.gender !== 'neutral' ? ` (${voice.gender})` : ''}`
                      : `${voice.name} (${voice.provider}${voice.gender ? ` - ${voice.gender}` : ''})`}
                  </option>
                ))}
                <option value="custom-json">Custom JSON</option>
              </select>
            {!loadingVoices && !voiceOk && (
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                {hasVoiceSelection(style) && !voiceMatchesProvider
                  ? `This ID is ${isFishVoiceId(style.selectedVoiceId) ? 'Fish Audio' : 'ElevenLabs'}. Pick a ${ttsProvider === 'fish' ? 'Fish Audio' : 'ElevenLabs'} voice to save.`
                  : 'Select a voice to enable Save.'}
              </p>
            )}
          </div>

          {style.selectedVoiceId === 'custom-json' && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Voice Configuration (JSON)
              </label>
              <textarea
                value={typeof style.voiceConfig === 'string' ? style.voiceConfig : (style.voiceConfig ? JSON.stringify(style.voiceConfig, null, 2) : '')}
                onChange={(e) => {
                  const value = e.target.value;
                  if (!value.trim()) {
                    // If empty, set to undefined instead of empty object
                    onStyleChange({ ...style, voiceConfig: '' });
                    return;
                  }
                  
                  onStyleChange({ ...style, voiceConfig: value });
                }}
                className="textarea min-h-[100px] font-mono text-sm"
                placeholder='Enter custom voice configuration as JSON\ne.g., {"provider":"11labs","voiceId":"..."}'
              />
              <p className="text-xs text-muted-foreground mt-1">
                Provide custom JSON configuration for voice settings.
              </p>
            </div>
          )}
        </div>

        {showTranscriber && (
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Speech recognition (how AI hears the customer)
            </label>
            <p className="text-xs text-muted-foreground mb-1">
              Not the speaking voice. Default Deepgram is fine for most calls.
            </p>
            <select
              value={style.transcriber || 'deepgram'}
              onChange={(e) =>
                onStyleChange({
                  ...style,
                  transcriber: e.target.value as 'deepgram' | '11labs' | 'assembly-ai' | 'azure' | 'gladia' | 'google' | 'openai'
                })
              }
              className="select"
            >
              <option value="deepgram">Deepgram</option>
              <option value="11labs">11Labs (STT only — not voice)</option>
              <option value="assembly-ai">Assembly AI</option>
              <option value="azure">Azure</option>
              <option value="gladia">Gladia</option>
              <option value="google">Google</option>
              <option value="openai">OpenAI</option>
            </select>
          </div>
        )}

        <div className="flex items-center">
          <Switch
            checked={style.isActive}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onStyleChange({ ...style, isActive: e.target.checked })
            }
          />
          <label className="ml-2 block text-sm text-foreground">
            Active (will be used in calls)
          </label>
        </div>

        <div className="flex justify-end space-x-3">
          <Button
            type="button"
            variant="outlined"
            onClick={onCancel}
          >
            <X className="h-4 w-4 mr-1 inline" />
            Cancel
          </Button>
          <Button
              type="button"
              variant="primary"
              onClick={() => onSave(style)}
              disabled={!canSave}
            >
              <Check className="h-4 w-4 mr-1 inline" />
              Save
            </Button>
        </div>
      </div>
    </div>
  );
};